import { z } from "zod";

import {
  ANNOTATION_COLORS,
  ANNOTATION_KINDS,
  ANNOTATION_SIDES,
  NOTE_BODY_FORMAT,
  PDF_NORMALIZED_COORDINATE_SYSTEM,
  isIsoUtc,
} from "@/domain";

const isoUtcSchema = z.string().refine(isIsoUtc, {
  message: "Expected a canonical ISO 8601 UTC timestamp.",
});

const sha256Schema = z
  .string()
  .regex(/^[0-9a-f]{64}$/, "Expected a lowercase SHA-256 hexadecimal digest.");

const pdfNormalizedCoordinateSchema = z.number().finite().min(0).max(1);

export const pdfNormalizedPointSchema = z
  .object({
    x: pdfNormalizedCoordinateSchema,
    y: pdfNormalizedCoordinateSchema,
  })
  .strict();

export const pdfNormalizedRectSchema = z
  .object({
    xMin: pdfNormalizedCoordinateSchema,
    yMin: pdfNormalizedCoordinateSchema,
    xMax: pdfNormalizedCoordinateSchema,
    yMax: pdfNormalizedCoordinateSchema,
  })
  .strict()
  .refine((rect) => rect.xMin <= rect.xMax && rect.yMin <= rect.yMax, {
    message: "Rectangle minimum coordinates must not exceed maximums.",
  });

export const textQuoteAnchorV1Schema = z
  .object({
    kind: z.literal("quote"),
    normalizationVersion: z.literal(1),
    exact: z.string(),
    normalized: z.string(),
    prefix: z.string(),
    suffix: z.string(),
    start: z.number().int().nonnegative(),
    end: z.number().int().nonnegative(),
  })
  .strict()
  .refine((anchor) => anchor.end >= anchor.start, {
    message: "Quote anchor end must not precede start.",
  });

export const textPositionAnchorV1Schema = z
  .object({
    kind: z.literal("position"),
    normalizationVersion: z.literal(1),
    position: z.number().int().nonnegative(),
    prefix: z.string(),
    suffix: z.string(),
  })
  .strict();

export const annotationTextAnchorV1Schema = z.discriminatedUnion("kind", [
  textQuoteAnchorV1Schema,
  textPositionAnchorV1Schema,
]);

export const annotationTargetV1Schema = z
  .object({
    text: annotationTextAnchorV1Schema.nullable(),
    geometry: z
      .object({
        coordinateSystem: z.literal(PDF_NORMALIZED_COORDINATE_SYSTEM),
        rects: z.array(pdfNormalizedRectSchema),
        point: pdfNormalizedPointSchema.nullable(),
      })
      .strict(),
  })
  .strict()
  .refine(
    (target) =>
      target.text !== null ||
      target.geometry.rects.length > 0 ||
      target.geometry.point !== null,
    {
      message: "An annotation target must contain text or geometry.",
    },
  );

export const marginaliaAnnotationV1Schema = z
  .object({
    id: z.string().uuid(),
    pageIndex: z.number().int().nonnegative(),
    kind: z.enum(ANNOTATION_KINDS),
    target: annotationTargetV1Schema,
    body: z
      .object({
        format: z.literal(NOTE_BODY_FORMAT),
        source: z.string(),
      })
      .strict()
      .nullable(),
    color: z.enum(ANNOTATION_COLORS).nullable(),
    preferredSide: z.enum(ANNOTATION_SIDES).nullable(),
    tags: z.array(z.string()),
    conflictOf: z.string().uuid().nullable(),
    revision: z.number().int().positive(),
    createdAt: isoUtcSchema,
    updatedAt: isoUtcSchema,
    deletedAt: isoUtcSchema.nullable(),
  })
  .strict()
  .refine(
    (annotation) => {
      const created = Date.parse(annotation.createdAt);
      const updated = Date.parse(annotation.updatedAt);
      const deleted =
        annotation.deletedAt === null ? null : Date.parse(annotation.deletedAt);
      return updated >= created && (deleted === null || deleted >= created);
    },
    {
      message:
        "Annotation updated/deleted timestamps must not precede creation.",
    },
  );

export const marginaliaExportDocumentV1Schema = z
  .object({
    sha256: sha256Schema,
    pdfFingerprint: z.string().nullable(),
    fileName: z.string(),
    title: z.string().nullable(),
    author: z.string().nullable(),
    byteLength: z.number().int().nonnegative(),
    pageCount: z.number().int().positive(),
  })
  .strict();

export const marginaliaReadingStateV1Schema = z
  .object({
    pageIndex: z.number().int().nonnegative(),
    scrollRatio: z.number().finite().min(0).max(1),
    zoom: z.number().finite().positive(),
  })
  .strict();

const marginaliaExportV1BaseSchema = z
  .object({
    format: z.literal("marginalia-annotations"),
    formatVersion: z.literal(1),
    applicationVersion: z.string().min(1),
    exportedAt: isoUtcSchema,
    document: marginaliaExportDocumentV1Schema,
    annotations: z.array(marginaliaAnnotationV1Schema),
    readingState: marginaliaReadingStateV1Schema.nullable(),
  })
  .strict();

function enforceUniqueAnnotationIds(
  bundle: {
    annotations: Array<{ id: string; pageIndex?: number }>;
    document?: { pageCount: number };
    readingState?: { pageIndex: number } | null;
  },
  context: z.RefinementCtx,
): void {
  const seen = new Set<string>();
  bundle.annotations.forEach((annotation, index) => {
    if (seen.has(annotation.id)) {
      context.addIssue({
        code: "custom",
        message: `Duplicate annotation ID ${annotation.id}.`,
        path: ["annotations", index, "id"],
      });
    }
    seen.add(annotation.id);

    if (
      bundle.document &&
      annotation.pageIndex !== undefined &&
      annotation.pageIndex >= bundle.document.pageCount
    ) {
      context.addIssue({
        code: "custom",
        message: "Annotation pageIndex exceeds the document page count.",
        path: ["annotations", index, "pageIndex"],
      });
    }
  });

  if (
    bundle.document &&
    bundle.readingState &&
    bundle.readingState.pageIndex >= bundle.document.pageCount
  ) {
    context.addIssue({
      code: "custom",
      message: "Reading-state pageIndex exceeds the document page count.",
      path: ["readingState", "pageIndex"],
    });
  }
}

export const marginaliaExportV1Schema =
  marginaliaExportV1BaseSchema.superRefine(enforceUniqueAnnotationIds);

const marginaliaLibraryDocumentV1Schema = marginaliaExportV1BaseSchema
  .omit({
    format: true,
    formatVersion: true,
    applicationVersion: true,
    exportedAt: true,
  })
  .strict()
  .superRefine(enforceUniqueAnnotationIds);

export const marginaliaLibraryExportV1Schema = z
  .object({
    format: z.literal("marginalia-library"),
    formatVersion: z.literal(1),
    applicationVersion: z.string().min(1),
    exportedAt: isoUtcSchema,
    documents: z.array(marginaliaLibraryDocumentV1Schema),
  })
  .strict()
  .superRefine((bundle, context) => {
    const seenDocumentHashes = new Set<string>();
    const seenAnnotationIds = new Set<string>();

    bundle.documents.forEach((document, documentIndex) => {
      if (seenDocumentHashes.has(document.document.sha256)) {
        context.addIssue({
          code: "custom",
          message: `Duplicate document SHA-256 ${document.document.sha256}.`,
          path: ["documents", documentIndex, "document", "sha256"],
        });
      }
      seenDocumentHashes.add(document.document.sha256);

      document.annotations.forEach((annotation, annotationIndex) => {
        if (seenAnnotationIds.has(annotation.id)) {
          context.addIssue({
            code: "custom",
            message: `Duplicate library annotation ID ${annotation.id}.`,
            path: [
              "documents",
              documentIndex,
              "annotations",
              annotationIndex,
              "id",
            ],
          });
        }
        seenAnnotationIds.add(annotation.id);
      });
    });
  });

export type MarginaliaAnnotationV1 = z.infer<
  typeof marginaliaAnnotationV1Schema
>;
export type MarginaliaExportDocumentV1 = z.infer<
  typeof marginaliaExportDocumentV1Schema
>;
export type MarginaliaReadingStateV1 = z.infer<
  typeof marginaliaReadingStateV1Schema
>;
export type MarginaliaExportV1 = z.infer<typeof marginaliaExportV1Schema>;
export type MarginaliaLibraryExportV1 = z.infer<
  typeof marginaliaLibraryExportV1Schema
>;
